import { useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { useHref, useNavigate } from 'react-router-dom';

import { DeviceType } from '../CSS';
import { MAX_TEXT_COMPONENT, ScrollDirection } from '../Article.context';
import styles from '../article.module.css';

import { VirtualArticleContext, LightboxArgs } from './VirtualArticleContext';
import { VirtualText } from './components/Text';
import { VirtualImage } from './components/Image';
import { VirtualAdvert } from './components/Offer';
import { VirtualDivider } from './components/Divider';
import { VirtualSpacer } from './components/Spacer';
import { VirtualGallery } from './components/Gallery';
import { VirtualVideo } from './components/Video';
import { VirtualColumns } from './components/Columns';
import { VirtualAnchor } from './components/Anchor';
import { VirtualAudio } from './components/Audio';
import { VirtualButton } from './components/Button';
import { VirtualCustom } from './components/Custom';
import { VirtualInfogram } from './components/Infogram';
import { VirtualInstagram } from './components/Instagram';
import { VirtualMap } from './components/Map';
import { VirtualTikTok } from './components/TikTok';
import { VirtualTwitter } from './components/Twitter';

import { reduceExcludeComponents } from '../../../constants';
import config from '../../../config';
import {
	AUTH_KEY,
	IS_SUBSCRIBED_KEY,
} from '../../../views/home/auth/Authentication.hooks';
import { AuthCognito } from '../../../config.type';

const textComponentsSet = new Set([
	'headline',
	'title',
	'subtitle',
	'intro',
	'body',
	'crosshead',
	'byline',
	'blockquote',
	'footer',
	'imagecaption',
]);

for (let i = 0; i <= MAX_TEXT_COMPONENT; i++) {
	textComponentsSet.add(`text${i + 1}`);
}

export function VirtualArticle({
	article,
	lang,
	device,
	index,
	className,
	isSelected,
	setNavbarVisibility,
	navigateToArticle,
	onLighboxOpen,
}: Readonly<VirtualArticleProps>) {
	const ref = useRef<any>();

	const { id, components, ArticleID } = article;
	const classNames = [styles['canvas'], 'canvas'];
	/*useDirectionScroll({
		ref,
		threshold: 100,
		isSelected,
		cb: (direction: ScrollDirection) => {
			if (!setScrollDirection) {
				return;
			}
			setScrollDirection(direction);
		},
	});*/

	const articleProps: MotionArticleProps = {
		internalRef: ref,
		id,
		ArticleID,
		index,
		className,
		classNames,
		components,
		setNavbarVisibility,
		device,
	};

	const contentProtection = config?.app?.contentProtection;

	const onContentProtection = (
		e: React.MouseEvent<HTMLImageElement, MouseEvent>,
	) => {
		e.preventDefault();
	};

	return (
		<VirtualArticleContext.Provider
			value={{
				lang,
				device,
				navigateToArticle,
				isSelected,
				onLighboxOpen,
				contentProtection: contentProtection,
				onContentProtection: contentProtection
					? onContentProtection
					: undefined,
			}}
		>
			{isSelected ? (
				<MotionArticle {...articleProps} />
			) : (
				<RegularArticle {...articleProps} />
			)}
		</VirtualArticleContext.Provider>
	);
}

function RegularArticle(props: Readonly<MotionArticleProps>) {
	// const { isSelected } = useContext(VirtualArticleContext);
	const {
		id,
		components,
		ArticleID,
		index,
		className,
		classNames,
		device,
	} = props;

	/* TODO Remove this if you want to disable componenets when the page is not selected
	let { components } = props;
	if (!isSelected) {
		components = [];
	}*/

	if (components === null) {
		return <SubscriptionRequired {...props} />;
	}

	return (
		<article
			id={`article-${id}`}
			data-article-id={ArticleID}
			data-index={index}
			className={className ?? ''}
		>
			<div className={classNames.join(' ')}>
				<div>
					{components
						? components
								.reduce(reduceExcludeComponents, [])
								.map(mapComponent(device))
						: null}
				</div>
			</div>
		</article>
	);
}

function SubscriptionRequired(props: Readonly<MotionArticleProps>) {
	const navigate = useNavigate();
	const basename = useHref('/callback');
	const { id, ArticleID, index, className, classNames } = props;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
		`state=${btoa(window.location.pathname)}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/login?', redirectSections].join(
		'',
	);

	if (!isAuthenticated) {
		return (
			<motion.article
				id={`article-${id}`}
				data-article-id={ArticleID}
				data-index={index}
				className={className ?? ''}
			>
				<div className={classNames.join(' ')}>
					<div className={styles['subscription-required']}>
						<section>
							<h1>Authentication Required</h1>
							<h2>Please sign in to read this article</h2>
							<button
								onClick={() => {
									location.href = redirectUrl;
								}}
							>
								{' '}
								Sign In
							</button>
						</section>
					</div>
				</div>
			</motion.article>
		);
	}

	if (!isSubscribed) {
		return (
			<motion.article
				id={`article-${id}`}
				data-article-id={ArticleID}
				data-index={index}
				className={className ?? ''}
			>
				<div className={classNames.join(' ')}>
					<div className={styles['subscription-required']}>
						<section>
							<h1>Subscription Required</h1>
							<h2>
								You need a valid subscription to view this
								article
							</h2>
							<button
								onClick={() => {
									navigate('/subscriptions');
								}}
							>
								{' '}
								Purchase Subscription
							</button>
						</section>
					</div>
				</div>
			</motion.article>
		);
	}

	return (
		<motion.article
			id={`article-${id}`}
			data-article-id={ArticleID}
			data-index={index}
			className={className ?? ''}
		>
			<div className={classNames.join(' ')}>
				<div className={styles['subscription-required']}>
					<section>
						<h1>Subscription Upgrade Required</h1>
						<h2>
							Please upgrade your subscription to view this
							article
						</h2>
						<button
							onClick={() => {
								navigate('/subscriptions');
							}}
						>
							{' '}
							Upgrade Subscription
						</button>
					</section>
				</div>
			</div>
		</motion.article>
	);
}

function MotionArticle(props: Readonly<MotionArticleProps>) {
	const {
		id,
		ArticleID,
		index,
		className,
		classNames,
		components,
		device,
	} = props;

	if (components === null) {
		return <SubscriptionRequired {...props} />;
	}

	return (
		<motion.article
			id={`article-${id}`}
			data-article-id={ArticleID}
			data-index={index}
			className={className ?? ''}
		>
			<div className={classNames.join(' ')}>
				<div>
					{components
						? components
								.reduce(reduceExcludeComponents, [])
								.map(mapComponent(device))
						: null}
				</div>
			</div>
		</motion.article>
	);
}

interface MotionArticleProps {
	internalRef: any;
	id: string;
	ArticleID: any;
	index: number;
	className: any;
	components: Array<any> | null;
	classNames: Array<any>;
	setNavbarVisibility: any;
	device: DeviceType;
}

interface VirtualArticleProps {
	article: any;
	index: number;
	lang: string;
	isSelected: boolean;
	device: DeviceType;
	className?: string;
	setScrollDirection?: (direction: ScrollDirection) => void;
	setNavbarVisibility: any;
	navigateToArticle?: (articleId: number) => void;
	onLighboxOpen: (props: LightboxArgs) => void;
}

function mapComponent(device: DeviceType) {
	return (component: any) => {
		const { devices } = component;
		if (
			devices &&
			devices[device === 'mobile' ? 'phone' : device] === 'off'
		) {
			return null;
		}
		const key = component.id;
		if (textComponentsSet.has(component.component)) {
			return <VirtualText key={key} {...component} />;
		}
		switch (component.component) {
			case 'image':
			case 'anscene':
				return <VirtualImage key={key} {...component} />;
			case 'advert':
				return <VirtualAdvert key={key} {...component} />;
			case 'columns':
				return (
					<VirtualColumns key={key} {...component}>
						{component.columns.map((c: any, index: number) => (
							<div
								key={`${key}-columns-${index}`}
								className="column"
							>
								{c.map(mapComponent(device))}
							</div>
						))}
					</VirtualColumns>
				);
			case 'divider':
				return <VirtualDivider key={key} {...component} />;
			case 'spacer':
				return <VirtualSpacer key={key} {...component} />;
			case 'gallery':
				return <VirtualGallery key={key} {...component} />;
			case 'video':
				return <VirtualVideo key={key} {...component} />;
			case 'anchor':
				return <VirtualAnchor key={key} {...component} />;
			case 'audio':
				return <VirtualAudio key={key} {...component} />;
			case 'button':
				return <VirtualButton key={key} {...component} />;
			case 'custom':
				return <VirtualCustom key={key} {...component} />;
			case 'infogram':
				return <VirtualInfogram key={key} {...component} />;
			case 'instagram':
				return <VirtualInstagram key={key} {...component} />;
			case 'map':
				return <VirtualMap key={key} {...component} />;
			case 'tiktok':
				return <VirtualTikTok key={key} {...component} />;
			case 'twitter':
				return <VirtualTwitter key={key} {...component} />;
		}

		return null;
	};
}

/**
 * React hook that says in which direction the scroll is moving
 *
 */
export function useDirectionScroll(args: UseScrollArgs) {
	const { ref, threshold, isSelected, cb } = args;
	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		let lastScrollY = ref.current.scrollTop;

		const updateScrollDir = (scrollY: number) => {
			if (Math.abs(scrollY - lastScrollY) < threshold) {
				return;
			}

			cb(scrollY > lastScrollY ? 'down' : 'up');
			lastScrollY = scrollY > 0 ? scrollY : 0;
		};

		const onScroll = (e: any) => {
			const { scrollTop } = e.target;
			updateScrollDir(scrollTop);
		};

		if (isSelected) {
			ref.current.addEventListener('scroll', onScroll);
		} else {
			ref.current.removeEventListener('scroll', onScroll);
		}
	}, [ref, isSelected]);
}

interface UseScrollArgs {
	ref: any;
	threshold: number;
	isSelected: boolean;
	cb: (direction: ScrollDirection) => void;
}
